import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import './LandingPage.css';
import './Legal.css';

const LAST_UPDATED = 'June 26, 2026';

/**
 * Privacy Policy + Data Disclosure.
 *
 * The "Data We Collect and Keep" table is derived directly from the platform's
 * actual data model (Mongoose schemas) so the disclosure is accurate. Written
 * in English as the authoritative version; a short Spanish summary note is shown
 * to es-locale users.
 */
const PrivacyPage = () => {
  const { language } = useLanguage();
  const isES = language === 'es';

  useEffect(() => {
    const prev = document.title;
    document.title = 'Privacy Policy & Data Disclosure — PHONESTREAM';
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="landing-page neo">
      <Header />

      <main className="landing-main legal-main">
        <article className="legal-doc">
          <span className="neo-eyebrow accent-cyan">Legal</span>
          <h1>Privacy Policy &amp; Data Disclosure</h1>
          <p className="legal-updated">Last updated: {LAST_UPDATED}</p>

          {isES && (
            <div className="legal-note">
              Esta Política de Privacidad se publica en inglés como versión oficial.
              A continuación encontrarás un resumen de los datos que conservamos y cómo
              los usamos. Si tienes dudas, contáctanos por los canales oficiales.
            </div>
          )}

          <p className="legal-lead">
            This Privacy Policy explains what information PHONESTREAM
            (“PhoneStream”, “we”, “us”) collects when you use the website,
            dashboard, NFT marketplace, store, SMM panel, games, and AI assistant
            (collectively, the “Platform”) at <a href="https://phonestream.online">phonestream.online</a>,
            why we collect it, who we share it with, and the choices you have.
          </p>
          <p>
            PHONESTREAM is a non-custodial, wallet-based platform. We do
            <strong> not</strong> ask for your name, email address, phone number,
            or government ID to create an account, and we never have access to your
            wallet’s private keys or seed phrase. Your primary identifier with us is
            your public blockchain wallet address.
          </p>

          <div className="legal-toc">
            <p className="legal-toc-title">Contents</p>
            <ol>
              <li>Who we are</li>
              <li>Data we collect and keep</li>
              <li>Public blockchain data</li>
              <li>How we use your data</li>
              <li>Third parties we share data with</li>
              <li>Cookies &amp; local storage</li>
              <li>Data retention</li>
              <li>Security</li>
              <li>Your rights &amp; choices</li>
              <li>Children</li>
              <li>International transfers</li>
              <li>Changes &amp; contact</li>
            </ol>
          </div>

          <h2>1. Who we are</h2>
          <p>
            PHONESTREAM operates a blockchain-based music-streaming, NFT, social
            media marketing (SMM), and gaming platform on Binance Smart Chain (BSC).
            We are the data controller for the personal data described below.
          </p>

          <h2>2. Data we collect and keep</h2>
          <p>
            We practice data minimization: we store what is necessary to operate the
            Platform. The table below is a complete, plain-language disclosure of the
            categories of data we keep, where it is stored, and why.
          </p>

          <div className="legal-table-wrap">
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Data we keep</th>
                  <th>Examples</th>
                  <th>Why we keep it</th>
                  <th>Stored in</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Wallet address</td>
                  <td>Your public BSC address; an authentication “nonce”</td>
                  <td>Your account identifier and login (wallet-signature auth)</td>
                  <td>Database</td>
                </tr>
                <tr>
                  <td>Spotify connection</td>
                  <td>Artist IDs you link; OAuth refresh token; API key</td>
                  <td>Fetch artist info, releases, and streaming analytics</td>
                  <td>Database (tokens encrypted, AES-256-GCM)</td>
                </tr>
                <tr>
                  <td>NFT &amp; streaming data</td>
                  <td>Token IDs you own, estimated streams, revenue figures</td>
                  <td>Show your holdings, dashboard analytics, revenue</td>
                  <td>Database + public chain</td>
                </tr>
                <tr>
                  <td>Orders &amp; purchases</td>
                  <td>SMM orders (service, link/URL, quantity, status), store purchases, transaction hashes, amounts</td>
                  <td>Fulfil and track your orders; provide receipts</td>
                  <td>Database + public chain</td>
                </tr>
                <tr>
                  <td>Escrow &amp; balances</td>
                  <td>USDT escrow credits, reserved amounts, game balances, deposits, refunds</td>
                  <td>Operate paid services and games; process refunds</td>
                  <td>Database + escrow smart contract</td>
                </tr>
                <tr>
                  <td>Game activity</td>
                  <td>Bets, results, multipliers, payouts, game session state</td>
                  <td>Run games provably and pay winnings; integrity/anti-abuse</td>
                  <td>Database</td>
                </tr>
                <tr>
                  <td>Support &amp; chat</td>
                  <td>Support messages, tickets, in-app notifications</td>
                  <td>Provide customer support and updates</td>
                  <td>Database</td>
                </tr>
                <tr>
                  <td>AI assistant</td>
                  <td>Your messages to the AI agent, tools it used, an audit log, rate counters, learned preferences</td>
                  <td>Operate the assistant, prevent abuse, and audit actions</td>
                  <td>Database (audit args/results truncated)</td>
                </tr>
                <tr>
                  <td>Technical logs</td>
                  <td>Server logs of requests/errors which may include your wallet address and IP at the time of a request</td>
                  <td>Security, debugging, fraud prevention</td>
                  <td>Server logs</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            We do <strong>not</strong> collect your real name, email, phone number,
            home address, or government identification, and we do not run third-party
            advertising or analytics trackers (no Google Analytics, no ad pixels).
          </p>

          <h2>3. Public blockchain data</h2>
          <p>
            PHONESTREAM runs on Binance Smart Chain. Transactions you make — minting
            NFTs, deposits, escrow reservations, purchases, payouts, and withdrawals —
            are recorded on a <strong>public, permanent, and immutable ledger</strong>.
            Your wallet address and those transactions are visible to anyone and are
            outside our control; we cannot edit or delete on-chain data.
          </p>

          <h2>4. How we use your data</h2>
          <ul>
            <li>To authenticate you and operate your account and dashboard.</li>
            <li>To fulfil SMM orders, store purchases, NFT minting, and game payouts.</li>
            <li>To hold and reconcile USDT escrow credits and process refunds.</li>
            <li>To fetch and display Spotify artist and streaming analytics you request.</li>
            <li>To provide customer support and send in-app notifications.</li>
            <li>To run the optional AI assistant and keep an audit trail of its actions.</li>
            <li>To maintain security, prevent fraud and abuse, and comply with law.</li>
          </ul>

          <h2>5. Third parties we share data with</h2>
          <p>
            We share the minimum necessary data with service providers (“processors”)
            to operate the Platform:
          </p>
          <ul>
            <li><strong>SMM providers</strong> (e.g. SMMFlare / MoreThanPanel) — receive the service ID, quantity, and the link/URL you submit to fulfil your marketing order.</li>
            <li><strong>Spotify</strong> — receives requests tied to the artist IDs you link, using your connected authorization, to return artist and track data.</li>
            <li><strong>Binance Smart Chain &amp; RPC node providers</strong> — process your on-chain transactions (publicly visible by design).</li>
            <li><strong>MongoDB Atlas</strong> — our managed cloud database host where the data above is stored.</li>
            <li><strong>AI model provider</strong> (e.g. OpenAI) — if you use the AI assistant, your messages and relevant context (including your wallet address) are sent to generate responses.</li>
          </ul>
          <p>
            We do <strong>not</strong> sell your personal data. We may disclose data if
            required by law, to enforce our Terms, or to protect the rights, safety, and
            property of users and the Platform.
          </p>

          <h2>6. Cookies &amp; local storage</h2>
          <p>
            We do not use advertising or tracking cookies. The Platform stores a small
            amount of data in your browser’s local storage — a session token (JWT) and
            your connected wallet address — so you stay signed in and your wallet
            reconnects. Clearing your browser storage or signing out removes these.
            Your language preference is also stored locally.
          </p>

          <h2>7. Data retention</h2>
          <p>
            We retain account, order, transaction, game, and support records for as long
            as your account is active and as needed for legal, accounting, anti-fraud,
            and dispute-resolution purposes. Some records (for example on-chain
            transactions and financial/refund ledgers) are kept for integrity and
            compliance and cannot be deleted on request. Short-lived security tokens are
            auto-deleted. On-chain data is permanent and cannot be erased by us.
          </p>

          <h2>8. Security</h2>
          <ul>
            <li>Sensitive secrets such as Spotify tokens are encrypted at rest using AES-256-GCM.</li>
            <li>Authentication is performed by wallet signature; we never receive your private keys or seed phrase.</li>
            <li>Access to production systems is restricted and data is hosted with a managed cloud provider.</li>
          </ul>
          <p>
            No method of transmission or storage is 100% secure. You are responsible for
            safeguarding your wallet, private keys, and seed phrase.
          </p>

          <h2>9. Your rights &amp; choices</h2>
          <p>
            Depending on your jurisdiction (for example under GDPR or similar laws), you
            may have rights to access, correct, export, or request deletion of the
            personal data we hold about you, and to object to or restrict certain
            processing. You can:
          </p>
          <ul>
            <li>Disconnect your Spotify connection to stop further Spotify data syncing.</li>
            <li>Stop using the AI assistant to stop generating new assistant records.</li>
            <li>Contact us via the official channels to request access or deletion of off-chain data we can lawfully remove.</li>
          </ul>
          <p>
            Note that we cannot delete public blockchain records, and we may need to
            retain certain data to meet legal and financial obligations.
          </p>

          <h2>10. Children</h2>
          <p>
            The Platform is not directed to anyone under 18 (or the age of majority in
            your jurisdiction). We do not knowingly collect data from minors.
          </p>

          <h2>11. International transfers</h2>
          <p>
            Our processors (including our cloud database, RPC nodes, Spotify, AI provider,
            and SMM providers) may process data in countries other than yours. Where
            required, we rely on appropriate safeguards for such transfers.
          </p>

          <h2>12. Changes &amp; contact</h2>
          <p>
            We may update this Privacy Policy to reflect legal, technical, or business
            changes; the “Last updated” date above will change accordingly. For any
            privacy questions or requests, contact the PHONESTREAM team through the
            official channels listed on the website or our community servers.
          </p>

          <div className="legal-note">
            This Privacy Policy should be read together with our{' '}
            <a href="/terms">Terms &amp; Conditions</a> and{' '}
            <a href="/msa">Master Service Agreement</a>.
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
